import { Form } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import TextLink from "@/components/text-link"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { SUPPORT_MAILTO } from "@/lib/support"
import { newIdentityPasswordResetPath, signInPath } from "@/routes"

export interface BrandSignInPanelProps {
  /** URL-borne `return_to` (read by the parent from the query string),
   *  forwarded as a hidden field so `SessionsController#create` honours
   *  the OKS-24 Variant B flow after the POST. */
  returnTo: string | null
  /** Swap back to the role-select panel (client-side, no reload). */
  onBack: () => void
}

/**
 * BrandSignInPanel — OKS-475 Brand branch (Admin / Brand / Agency staff).
 *
 * This is the **pre-existing** `sessions/new` email/password form
 * re-housed inside the role-gated layout — NOT a rewrite of staff auth.
 * The submit target (`signInPath()`), field `name` attributes
 * (`email` / `password`), `return_to` hidden field, "Forgot password?"
 * link, `processing` spinner, and `errors` render-prop are all preserved
 * exactly so the staff login path does NOT regress (no change to
 * `SessionsController`; bad-credentials still surface via the existing
 * flash toast). The selector contract (`name="email"`,
 * `name="password"`, `form button[type=submit]`,
 * `data-testid="sessions-new-return-to"`) is held stable for the
 * sessions request specs + Playwright `signIn()` fixture.
 */
export default function BrandSignInPanel({
  returnTo,
  onBack,
}: BrandSignInPanelProps) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-6" data-testid="login-brand-panel">
      <div className="space-y-1 text-center">
        <h1 className="text-xl font-medium">{t("login.brand.title")}</h1>
        <p className="text-muted-foreground text-sm">
          {t("login.brand.subtitle")}
        </p>
      </div>

      <Form
        method="post"
        action={signInPath()}
        resetOnSuccess={["password"]}
        className="flex flex-col gap-6"
      >
        {({ processing, errors }) => (
          <>
            {/*
              Forward the URL-borne `return_to` as a hidden form field so
              `SessionsController#create` can read it from
              `params[:return_to]` after the POST (OKS-24 Variant B).
              Backend's `RETURN_TO_ALLOWLIST` validates the value.
            */}
            {returnTo !== null && (
              <input
                type="hidden"
                name="return_to"
                value={returnTo}
                data-testid="sessions-new-return-to"
              />
            )}
            <div className="grid gap-6">
              <div className="grid gap-2">
                <Label htmlFor="email">
                  {t("sessions_new.email_label")}
                  <span className="text-destructive ml-0.5" aria-hidden="true">
                    *
                  </span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  aria-required="true"
                  aria-invalid={Boolean(errors.email)}
                  autoFocus
                  tabIndex={1}
                  autoComplete="email"
                  placeholder={t("sessions_new.email_placeholder")}
                />
                <InputError messages={errors.email} />
              </div>

              <div className="grid gap-2">
                <div className="flex items-center">
                  <Label htmlFor="password">
                    {t("sessions_new.password_label")}
                    <span
                      className="text-destructive ml-0.5"
                      aria-hidden="true"
                    >
                      *
                    </span>
                  </Label>
                  <TextLink
                    href={newIdentityPasswordResetPath()}
                    className="ml-auto text-sm"
                    tabIndex={5}
                  >
                    {t("sessions_new.forgot_password")}
                  </TextLink>
                </div>
                <Input
                  id="password"
                  type="password"
                  name="password"
                  aria-required="true"
                  aria-invalid={Boolean(errors.password)}
                  tabIndex={2}
                  autoComplete="current-password"
                  placeholder={t("sessions_new.password_placeholder")}
                />
                <InputError messages={errors.password} />
              </div>

              <Button
                type="submit"
                className="mt-4 w-full"
                tabIndex={4}
                disabled={processing}
              >
                {processing && <Spinner />}
                {t("sessions_new.submit")}
              </Button>
            </div>
          </>
        )}
      </Form>

      <div className="flex flex-col items-center gap-3 text-sm">
        <a
          href={SUPPORT_MAILTO}
          className="text-muted-foreground hover:text-foreground text-center underline decoration-neutral-300 underline-offset-4"
          data-testid="login-brand-contact-oks"
        >
          {t("login.brand.contact_oks")}
        </a>
        <button
          type="button"
          onClick={onBack}
          className="text-muted-foreground hover:text-foreground"
          data-testid="login-brand-back"
        >
          {t("login.brand.back")}
        </button>
      </div>
    </div>
  )
}
